import { useRef, useState, useEffect, useCallback } from "react";
import { Mic, Square, Play, Pause, Send, Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PILLARS } from "../lib/constants";

export default function LiveMic() {
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const mimeRef = useRef("audio/webm");

  const pickMime = () => {
    if (typeof window === "undefined" || !window.MediaRecorder) return "";
    const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg", "audio/ogg"];
    for (const t of types) {
      try { if (MediaRecorder.isTypeSupported(t)) return t; } catch { /* ignore */ }
    }
    return "";
  };

  const extFor = (mime) =>
    mime.includes("mp4") ? "m4a" : mime.includes("mpeg") ? "mp3" : mime.includes("ogg") ? "ogg" : "webm";

  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [db, setDb] = useState(0);
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("citizen_voices");
  const [submitting, setSubmitting] = useState(false);
  const playbackRef = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext("2d");
    const bufferLen = analyser.frequencyBinCount;
    const data = new Uint8Array(bufferLen);

    const render = () => {
      rafRef.current = requestAnimationFrame(render);
      analyser.getByteTimeDomainData(data);

      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = "#050B14";
      ctx.fillRect(0, 0, w, h);

      // decibel-ish level
      let sum = 0;
      for (let i = 0; i < bufferLen; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / bufferLen);
      setDb(Math.min(100, Math.round(rms * 240)));

      // waveform
      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, "#0066FF");
      grad.addColorStop(0.5, "#2B82FF");
      grad.addColorStop(1, "#FF6B00");
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = grad;
      ctx.shadowBlur = 12;
      ctx.shadowColor = "#0066FF";
      ctx.beginPath();
      const slice = w / bufferLen;
      let x = 0;
      for (let i = 0; i < bufferLen; i++) {
        const y = (data[i] / 255) * h;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += slice;
      }
      ctx.lineTo(w, h / 2);
      ctx.stroke();
    };
    render();
  }, []);

  const startRecording = async () => {
    setError("");
    if (audioURL) resetRecording();
    if (typeof window === "undefined" || !window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
      setError("In-browser recording isn't supported on this browser. Please use Chrome, or update your iPhone to a newer iOS version.");
      toast.error("Recording not supported on this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new AudioCtx();
      if (audioCtxRef.current.state === "suspended") await audioCtxRef.current.resume();
      const source = audioCtxRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioCtxRef.current.createAnalyser();
      analyserRef.current.fftSize = 1024;
      source.connect(analyserRef.current);

      const mime = pickMime();
      mimeRef.current = mime || "audio/webm";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        setAudioBlob(blob);
        setAudioURL(URL.createObjectURL(blob));
      };
      mr.start();
      mediaRecorderRef.current = mr;

      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      draw();
    } catch (e) {
      setError("Microphone access was blocked. Please allow mic permissions in your browser and try again.");
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    cancelAnimationFrame(rafRef.current);
    clearInterval(timerRef.current);
    audioCtxRef.current?.close();
    setRecording(false);
    setDb(0);
  };

  const resetRecording = () => {
    setAudioURL(null);
    setAudioBlob(null);
    setSeconds(0);
    setPlaying(false);
  };

  const togglePlayback = () => {
    const el = playbackRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      const p = el.play();
      if (p && p.then) {
        p.then(() => setPlaying(true)).catch(() => {
          setPlaying(false);
          toast.error("This browser can't play the recording back, but it's saved — tap Submit to send it.");
        });
      } else {
        setPlaying(true);
      }
    }
  };

  const submit = async () => {
    if (!audioBlob) return;
    if (!name.trim()) { toast.error("Please add your name (or 'Anonymous')"); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("category", category);
      form.append("story", "Voice note recorded via Live Operative Mic");
      form.append("audio", audioBlob, `voicenote-${Date.now()}.${extFor(mimeRef.current)}`);
      const res = await api.post("/submissions", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      resetRecording();
      setName("");
    } catch (e) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <section id="live-mic" className="relative py-24 bg-[#050B14] grain overflow-hidden" data-testid="live-mic-section">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-[#0066FF]/10 blur-3xl" />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-12">
          <div className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#FF6B00] mb-3 flex items-center justify-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#FF3B30] animate-pulse" /> Live Operative Mic
          </div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">
            Grab The <span className="text-[#0066FF] blue-glow">Mic</span>
          </h2>
          <p className="mt-4 text-[#94A3B8] max-w-xl mx-auto">
            Record your voice right here in your browser. Tell your story, then send it straight to the
            show for review.
          </p>
        </div>

        <div className="rounded-2xl border border-[#0066FF]/25 bg-[#0C1424] p-6 sm:p-9 blue-box-glow">
          {/* Visualizer */}
          <div className="relative rounded-xl overflow-hidden border border-[#0066FF]/20 bg-[#050B14]">
            <canvas
              ref={canvasRef}
              width={1000}
              height={200}
              data-testid="mic-audio-waveform-canvas"
              className="w-full h-40 sm:h-48"
            />
            <div className="absolute top-3 left-4 font-mono2 text-xs tracking-widest uppercase flex items-center gap-2">
              {recording ? (
                <span className="flex items-center gap-2 text-[#FF3B30]">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FF3B30] animate-pulse" /> Rec
                </span>
              ) : (
                <span className="text-[#94A3B8]">Standby</span>
              )}
            </div>
            <div className="absolute top-3 right-4 font-mono2 text-lg text-[#0066FF]" data-testid="mic-timer">
              {fmt(seconds)}
            </div>
            {/* Decibel meter */}
            <div className="absolute bottom-3 left-4 right-4 flex items-center gap-3">
              <span className="font-mono2 text-[10px] text-[#94A3B8] uppercase">Level</span>
              <div className="flex-1 h-1.5 rounded-full bg-[#121D33] overflow-hidden" data-testid="mic-decibel-meter">
                <div
                  className="h-full rounded-full transition-all duration-75"
                  style={{
                    width: `${db}%`,
                    background: db > 70 ? "#FF6B00" : "#0066FF",
                  }}
                />
              </div>
              <span className="font-mono2 text-[10px] text-[#0066FF] w-8 text-right">{db}</span>
            </div>
          </div>

          {error && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 text-sm text-[#FF9A94]">
              <AlertCircle className="h-5 w-5 shrink-0" /> {error}
            </div>
          )}

          {/* Controls */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            {!recording ? (
              <button
                onClick={startRecording}
                data-testid="mic-start-recording-button"
                className="flex items-center gap-2 rounded-full bg-[#FF3B30] hover:bg-[#ff544a] transition-colors px-8 py-4 font-cond font-bold uppercase tracking-wide text-white amber-glow"
              >
                <Mic className="h-5 w-5" /> {audioURL ? "Record Again" : "Start Recording"}
              </button>
            ) : (
              <button
                onClick={stopRecording}
                data-testid="mic-stop-recording-button"
                className="flex items-center gap-2 rounded-full bg-white text-[#050B14] px-8 py-4 font-cond font-bold uppercase tracking-wide hover:bg-[#e2e8f0] transition-colors"
              >
                <Square className="h-5 w-5 fill-[#050B14]" /> Stop
              </button>
            )}

            {audioURL && !recording && (
              <>
                <button
                  onClick={togglePlayback}
                  data-testid="mic-playback-toggle"
                  className="flex items-center gap-2 rounded-full border border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white transition-colors px-6 py-4 font-cond font-bold uppercase"
                >
                  {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                  {playing ? "Pause" : "Play Back"}
                </button>
                <button
                  onClick={resetRecording}
                  data-testid="mic-discard-button"
                  className="flex items-center gap-2 rounded-full text-[#94A3B8] hover:text-[#FF3B30] transition-colors px-4 py-4 font-cond font-bold uppercase"
                >
                  <Trash2 className="h-5 w-5" /> Discard
                </button>
              </>
            )}
          </div>

          <audio
            ref={playbackRef}
            src={audioURL || undefined}
            playsInline
            onEnded={() => setPlaying(false)}
            data-testid="mic-playback-audio-player"
            className="hidden"
          />

          {/* Submit form */}
          {audioURL && !recording && (
            <div className="mt-8 border-t border-[#0066FF]/15 pt-6 grid sm:grid-cols-3 gap-4 items-end">
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5">Your Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Anonymous"
                  data-testid="mic-name-input"
                  className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3 py-2.5 text-sm outline-none focus:border-[#0066FF]"
                />
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5">Topic</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="mic-category-select"
                  className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3 py-2.5 text-sm outline-none focus:border-[#0066FF]"
                >
                  {PILLARS.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="mic-submit-voice-note-button"
                className="flex items-center justify-center gap-2 rounded-lg bg-[#0066FF] hover:bg-[#2B82FF] disabled:opacity-60 transition-colors px-5 py-2.5 font-cond font-bold uppercase tracking-wide text-white"
              >
                <Send className="h-4 w-4" /> {submitting ? "Sending..." : "Submit Voice Note"}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
